import { useState } from 'react';
import { ChevronDown, ChevronRight, EllipsisVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ResourceSet } from 'components/IAP/hooks/resourceSetHooks';
import { NULL_VALUE } from 'constants/displayConstants';
import { EventMapButton } from './EventMapButton';

type EventAssetCardProps = {
  name: string;
  resourceSet: ResourceSet | undefined;
  disabled?: boolean;
  onRemove: () => void;
};

export const EventAssetCard: React.FC<EventAssetCardProps> = ({
  name,
  resourceSet,
  disabled,
  onRemove,
}) => {
  const [open, setOpen] = useState(false);
  const ChevronIcon = open ? ChevronDown : ChevronRight;

  const quantity =
    resourceSet?.quantity != null
      ? [resourceSet.quantity, resourceSet.unit]
          .filter((part) => part != null && part !== '')
          .join(' ')
      : NULL_VALUE;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="tw:rounded-lg tw:border tw:border-border"
    >
      <div className="tw:flex tw:items-center tw:gap-2 tw:p-2">
        <CollapsibleTrigger className="tw:flex tw:flex-1 tw:items-center tw:gap-2 tw:bg-transparent tw:p-0 tw:text-left tw:font-semibold tw:text-foreground">
          <ChevronIcon className="tw:size-4 tw:shrink-0" aria-hidden />
          {name}
        </CollapsibleTrigger>
        {resourceSet?.geometry && (
          <EventMapButton name={name} geometry={resourceSet.geometry} />
        )}
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`${name} options`}
              />
            }
          >
            <EllipsisVertical />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              variant="destructive"
              disabled={disabled}
              onClick={onRemove}
            >
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <CollapsibleContent>
        <div className="tw:grid tw:gap-3 tw:border-t tw:border-border tw:p-3">
          <div className="tw:grid tw:grid-cols-2 tw:gap-3">
            <div>
              <div className="tw:font-semibold">Current Location</div>
              <div>{resourceSet?.address || NULL_VALUE}</div>
            </div>
            <div>
              <div className="tw:font-semibold">Quantity</div>
              <div>{quantity}</div>
            </div>
          </div>
          <div>
            <div className="tw:font-semibold">Capabilities</div>
            <div className="tw:whitespace-pre-wrap">
              {resourceSet?.description || NULL_VALUE}
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
